import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { Trans } from "react-i18next";
import { BrowserView, isMobile, MobileView } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import AdvanceSearch from "../components/AdvanceSearch";
import FilterCard from "../components/FilterCard";
import SearchResult from "../components/SearchResult";
import Search from "../components/Search";
import { API } from "../constants/api";
import { PAGE } from "../constants/page";
import SuggestionContext from "../contexts/SuggestionContext";
import HttpService from "../services/HttpService";
import PatientList from "../components/PatientList";
import Utility from "../utility/Utility";
import BreadcrumbService from "../services/BreadcrumbsService";
import Pagination from "../components/Pagination";
import { ApiCallWithLoader, errorUpdate } from "../redux/reducers/LoaderSlice";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import "../styles/home.scss";
import {
  patientDetailsState,
  patientName,
  updateSearchStatus,
} from "../redux/reducers/PatientSlice";
import SuccessMessage from "../components/SuccessMessage";
import { UserState } from "../redux/reducers/UserSlice";
import moment from "moment";

const Home = () => {
  const httpService = HttpService.instance;
  const breadcrumbService = BreadcrumbService.instance;
  const navigate = useNavigate();
  const [searchData, setSearchData] = useState<any>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any[]>([]);
  const [searchSuggestions, setSearchSuggestion] = useState<any>([]);
  const [advancedSearch, setAdvancedSearch] = useState<any>(null);
  const [quickSearch, setQuickSearch] = useState<any>(null);
  const [updatedData, setUpdatedData] = useState(false);
  const [showAlert, setShowAlert] = useState(true);
  const resultPerPage = isMobile ? 5 : 10;
  const [pageNumber, setPageNumber] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const dispatch: any = useAppDispatch();
  const userDetails: any = useAppSelector(UserState);
  const userStateName =
    userDetails?.userDetails?.user_metadata?.address?.region;
  const userVillageName =
    userDetails?.userDetails?.user_metadata?.address?.locality?.split(",")[0];
  let searchTimer: NodeJS.Timeout | null = null;
  const patientDetails: any = useAppSelector(patientDetailsState);
  useEffect(() => {
    // check if the application loaded insided iframe
    if (Utility.inIframe()) {
      window.parent.postMessage(
        {
          func: "parentFunc",
          message: "contentLoadError",
        },
        "*"
      );
    }

    if (
      breadcrumbService.advancedSearch !== null &&
      breadcrumbService.active_key !== ""
    ) {
      handleOnAdvanceSearch(breadcrumbService.advancedSearch);
    } else {
      recentlyAddedPatients();
    }
    window.addEventListener("popstate", handleBrowserBackButton);

    return () => {
      //get last patientId
      const previousPatientId = localStorage.getItem(
        "previouslyAddedPatientId"
      );
      // set new patient id
      localStorage.setItem(
        "previouslyAddedPatientId",
        patientDetails.newPatientId || ""
      );

      if (
        previousPatientId === "" ||
        patientDetails.newPatientId === previousPatientId
      ) {
        // hide success message
        dispatch(patientName({ firstName: null, lastName: null }));
      }
      window.removeEventListener("popstate", handleBrowserBackButton);
    };
  }, []);

  const handleBrowserBackButton = async (e: any) => {
    // to avoid back when on home page
    e.preventDefault();
    navigate(PAGE.HOME);
  };

  const resetSuggestionList = () => {
    setSearchSuggestion([]);
  };

  const handleOnSearchInput = (searchQuery: string) => {
    // Implementing timer for search input so api doesnot call on every frequent input
    const newSearchTimer = setTimeout(() => {
      fetchSearchResult(searchQuery);
    }, 500);

    if (searchTimer) {
      clearTimeout(searchTimer);
    }

    searchTimer = newSearchTimer;
  };

  const fetchSearchResult = async (searchQuery: string) => {
    // Moved functionality from handleOnSearchInput so function is called according to timer
    const searchText = parseInt(searchQuery);
    let url = "";
    if (searchQuery.length < 3) {
      setSearchSuggestion([]);
      return;
    }
    url =
      API.SEARCH + "?name:contains=" + searchQuery.trim().split(" ").join(",");
    if (searchQuery.length > 9 && !isNaN(searchText)) {
      url = API.SEARCH + "?phone=" + searchQuery;
    }
    url =
      url +
      "&_sort=-_lastUpdated" +
      `&address-state=${userStateName}&address-city=${userVillageName}`;
    try {
      const quickSearchResult = await httpService.callAPI(url, "GET");
      setSearchSuggestion(quickSearchResult?.entry);
      setShowAlert(false);
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  };

  const handleAddPatient = () => {
    breadcrumbService.advancedSearch = null;
    navigateTo(PAGE.NEW_PATIENT);
  };

  const handleOnSearch = async (search: any, manualCall: boolean = false) => {
    dispatch(patientName({ firstName: null, lastName: null }));

    setPageNumber(1);
    let searchQuery = "";
    if (typeof search === "object") {
      breadcrumbService.advancedSearch = search;
      const keysArr = Object.keys(search);
      const filterArr = [];
      for (var i = 0; i < keysArr.length; i++) {
        if (search[keysArr[i]] !== undefined && search[keysArr[i]] !== "") {
          filterArr.push({
            key: keysArr[i],
            value:
              keysArr[i] == "birthdate"
                ? moment(search[keysArr[i]], "YYYY-MM-DD").format("DD/MM/YYYY")
                : search[keysArr[i]],
          });
        }
      }

      if (filterArr.length > 0) {
        searchQuery = Utility.objectToQueryString(search) + `&_page=1`;
        setAdvancedSearch(search);
        setQuickSearch(null);
        setSearchFilters(filterArr);
      } else {
        searchQuery = `?_page=1`;
        setSearchFilters(filterArr);
        navigate(PAGE.HOME);
      }
    } else {
      const searchOption = isNaN(search) ? "name:contains" : "phone";
      // /\s/.test(search) && (search = Utility.getSearchName(search));
      /\s/.test(search) && (search = search.split(" ").join(","));
      searchQuery = `?${searchOption}=${search}&_page=1`;
      setQuickSearch(`?${searchOption}=${search}`);
      setAdvancedSearch(null);
      setSearchFilters([]);
    }
    resetSuggestionList();

    //TODO: remove once working
    // searchQuery =
    //   "?birthDate=2022-02-10&email=abc@gmail.com&phone=1234564561&name=abc";
    const url =
      `${API.SEARCH}` +
      `${searchQuery}` +
      "&_sort=-_lastUpdated" +
      `&address-state=${userStateName}&_count=${resultPerPage}&address-city=${userVillageName}`;
    const requestDetails = {
      url,
      method: "GET",
    };
    const countUrl = url + "&_total=accurate&_count=1";
    const countDetails = {
      url: countUrl,
      method: "GET",
    };
    try {
      const searchResult = await dispatch(ApiCallWithLoader(requestDetails));
      const getCount = await dispatch(ApiCallWithLoader(countDetails));
      setSearchData(searchResult.payload?.entry || []);
      //setPaginationLinks(searchResult.result?.link || []);
      setTotalResults(getCount.payload?.total || 0);
      setShowAlert(false);
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
    setUpdatedData(manualCall);
    resetSuggestionList();
  };

  const handleOnAdvanceSearch = async (search: any) => {
    dispatch(patientName({ firstName: null, lastName: null }));

    setPageNumber(1);
    let searchQuery = "";
    if (typeof search === "object") {
      breadcrumbService.advancedSearch = search;
      const keysArr = Object.keys(search);
      const filterArr = [];
      for (var i = 0; i < keysArr.length; i++) {
        if (search[keysArr[i]] !== undefined && search[keysArr[i]] !== "") {
          filterArr.push({
            key: keysArr[i],
            value:
              keysArr[i] == "birthdate"
                ? moment(search[keysArr[i]], "YYYY-MM-DD").format("DD/MM/YYYY")
                : search[keysArr[i]],
          });
        }
      }

      if (filterArr.length > 0) {
        searchQuery = Utility.objectToQueryString(search) + `&_page=1`;
        setAdvancedSearch(search);
        setQuickSearch(null);
        setSearchFilters(filterArr);
      } else {
        searchQuery = `?_page=1`;
        setSearchFilters(filterArr);
        navigate(PAGE.HOME);
      }
    } else {
      const searchOption = isNaN(search) ? "name:contains" : "phone";
      // /\s/.test(search) && (search = Utility.getSearchName(search));
      /\s/.test(search) && (search = search.split(" ").join(","));
      searchQuery = `?${searchOption}=${search}&_page=1`;
      setQuickSearch(`?${searchOption}=${search}`);
      setAdvancedSearch(null);
      setSearchFilters([]);
    }
    resetSuggestionList();

    //TODO: remove once working
    // searchQuery =
    //   "?birthDate=2022-02-10&email=abc@gmail.com&phone=1234564561&name=abc";
    const url =
      `${API.SEARCH}` +
      `${searchQuery}` +
      "&_sort=-_lastUpdated" +
      `&address-state=${userStateName}&_count=${resultPerPage}`;
    const requestDetails = {
      url,
      method: "GET",
    };
    const countUrl = url + "&_total=accurate&_count=1";
    const countDetails = {
      url: countUrl,
      method: "GET",
    };
    try {
      const searchResult = await dispatch(ApiCallWithLoader(requestDetails));
      const getCount = await dispatch(ApiCallWithLoader(countDetails));
      setSearchData(searchResult.payload?.entry || []);
      //setPaginationLinks(searchResult.result?.link || []);
      setTotalResults(getCount.payload?.total || 0);
      setShowAlert(false);
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
    setUpdatedData(false);
    resetSuggestionList();
  };

  const handleFilterClose = (filterObj: any) => {
    delete advancedSearch[filterObj.key];
    if (searchFilters.length == 1) {
      handleOnSearch("", true);
    } else {
      handleOnAdvanceSearch(advancedSearch);
    }
  };

  const handlePageChange = async (e: any) => {
    let url: string;
    setPageNumber(e.selected + 1);
    const queryParams = advancedSearch
      ? Utility.objectToQueryString(advancedSearch)
      : quickSearch
      ? quickSearch
      : "?";

    if (patientDetails.isAdvanceSearch) {
      const paginParams = `&_page=${
        e.selected + 1
      }&_count=${resultPerPage}&_getpagesoffset=${e.selected * resultPerPage}`;
      const searchQuery = queryParams + paginParams;
      url =
        `${API.SEARCH}` +
        `${searchQuery}` +
        `&_sort=-_lastUpdated` +
        `&address-state=${userStateName}`;
    } else {
      const paginParams = `&_page=${
        e.selected + 1
      }&_count=${resultPerPage}&_getpagesoffset=${e.selected * resultPerPage}`;
      const searchQuery = queryParams + paginParams;
      url =
        `${API.SEARCH}` +
        `${searchQuery}` +
        `&_sort=-_lastUpdated` +
        `&address-state=${userStateName}&address-city=${userVillageName}`;
    }

    const requestDetails = {
      url,
      method: "GET",
    };
    try {
      const searchResult = await dispatch(ApiCallWithLoader(requestDetails));
      setSearchData(searchResult.payload?.entry || []);
      setTotalRecords(searchResult?.payload?.total);
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  };

  const navigateTo = (url: string) => {
    navigate(url);
  };

  const recentlyAddedPatients = async () => {
    let searchQuerry = `?_sort=-_lastUpdated&_total=accurate&_page=${pageNumber}&_count=${resultPerPage}&address-state=${userStateName}&address-city=${userVillageName}`;
    const url = API.SEARCH + searchQuerry;
    const requestDetails = {
      url,
      method: "GET",
    };
    setUpdatedData(true);
    setPageNumber(1);
    try {
      const recentlyAddedRecords = await dispatch(
        ApiCallWithLoader(requestDetails)
      );

      const _totalRecords = recentlyAddedRecords?.payload?.total;
      setTotalRecords(_totalRecords);
      setSearchData(recentlyAddedRecords?.payload?.entry || []);
      setTotalResults(_totalRecords);
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  };

  return (
    <div className="home">
      <div className="bg-light-gray">
        <div className="container p-0">
          <SuggestionContext.Provider
            value={{
              searchSuggestions,
              setSearchSuggestion,
            }}
          >
            <Search onInput={handleOnSearchInput} onSearch={handleOnSearch} />
          </SuggestionContext.Provider>
        </div>
      </div>
      <div className="container pb-0 marbtm12">
        <div className={`row ${searchFilters.length > 0 ? "mt-2" : ""}`}>
          <div className="col-md-4"></div>
          <div className="col-md-8">
            <div className="searchFilters">
              {searchFilters.map((filter, index) => {
                return (
                  <FilterCard
                    key={`filter-${index}`}
                    label={filter.value}
                    altKey={index}
                    onClose={() => handleFilterClose(filter)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <BrowserView>
        <div className="container pt-0">
          {patientDetails.firstName !== null &&
          patientDetails.patientUpdated === false &&
          showAlert ? (
            <SuccessMessage
              patientAdded={
                patientDetails.firstName + " " + patientDetails.lastName
              }
            />
          ) : null}
          <div className="row">
            <div className="col-md-4">
              <span className="heading-bold">
                <Trans i18nKey={"advanced_search"}></Trans>
              </span>
            </div>
            <div className="col-md-8">
              <div className="row align-items-center">
                <div className="col-md-8">
                  {!updatedData ? (
                    <span className="heading-bold ps-3">
                      {totalResults}{" "}
                      <Trans
                        i18nKey={
                          totalResults === 1
                            ? "one_result_found"
                            : "result_found"
                        }
                      ></Trans>
                    </span>
                  ) : (
                    <span className="heading-bold ps-3">
                      <Trans i18nKey={"patient_list"}></Trans>
                    </span>
                  )}
                </div>
                <div className="col-md-4 text-end">
                  <Button onClick={handleAddPatient}>
                    <Trans i18nKey={"add_patient"}></Trans>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </BrowserView>
      <MobileView>
        <div className="container pt-0">
          {patientDetails.firstName !== null &&
          patientDetails.patientUpdated === false &&
          showAlert ? (
            <SuccessMessage
              patientAdded={
                patientDetails.firstName + " " + patientDetails.lastName
              }
            />
          ) : null}
          <div className="align-mob">
            <div>
              {!updatedData ? (
                <span className="heading-bold px-xs-0">
                  {totalResults}{" "}
                  <Trans
                    i18nKey={
                      totalResults === 1 ? "one_result_found" : "result_found"
                    }
                  ></Trans>
                </span>
              ) : (
                <span className="heading-bold px-xs-0 beneficiaries-header">
                  <Trans i18nKey={"patient_list"}></Trans>
                </span>
              )}
            </div>
            <div className="text-end beneficiaries-button">
              <Button onClick={() => navigateTo(PAGE.NEW_PATIENT)}>
                <Trans i18nKey={"add_patient"}></Trans>
              </Button>
            </div>
          </div>
        </div>
      </MobileView>

      <div className="container pt-0">
        <div className="row">
          {!isMobile && (
            <div
              className={`col-md-4 pb-3 pt-3 left bg-light-gray marginBtmSet`}
            >
              <AdvanceSearch
                onSearchClick={handleOnAdvanceSearch}
                onCloseClick={null}
              />
            </div>
          )}

          <div className={`col-md-8 right ${isMobile ? "" : "ps-4"} `}>
            <main>
              {searchData?.length > 0 ? (
                <>
                  <SearchResult data={searchData || []} />
                  <div>
                    <Pagination
                      totalResultsFound={totalResults}
                      totalRecords={totalRecords}
                      pageNumber={pageNumber}
                      pageCount={Math.ceil(totalResults / resultPerPage)}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </>
              ) : (
                <PatientList
                  list={[]}
                  isAdvSearch={searchFilters.length ? true : false}
                />
              )}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
